import { IconCoins, IconGift, IconSettings } from '@tabler/icons-react';
import { Link, useLocation } from '@tanstack/react-router';
import { useState } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from '@/components/ui/popover';
import { DAILY_CHECK_IN_ENABLED } from '@/config/credit-economics';
import { DAILY_CHECK_IN_JOURNEY_DAYS } from '@/credits/daily-check-in-policy';
import { useDailyCheckIn } from '@/hooks/use-daily-check-in';
import { formatCreditsBalance, useCredits } from '@/hooks/use-credits';
import { openDailyCheckInDialog } from '@/lib/daily-check-in-dialog';
import { getLocale } from '@/lib/locale';
import { Routes } from '@/lib/routes';
import { cn } from '@/lib/utils';
import { m } from '@/locale/paraglide/messages';

interface CreditBadgeProps {
  className?: string;
}

export function CreditBadge({ className }: CreditBadgeProps) {
  const [open, setOpen] = useState(false);
  const creditsQuery = useCredits();
  const checkInQuery = useDailyCheckIn();
  const location = useLocation();
  const locale = getLocale();
  const balanceLabel = formatCreditsBalance(creditsQuery.data?.balance);
  const completedDays = checkInQuery.data?.completedDays ?? 0;
  const progress = Math.min(
    100,
    (completedDays / DAILY_CHECK_IN_JOURNEY_DAYS) * 100
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        className={cn(
          'flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-sm transition-colors hover:bg-accent',
          className
        )}
        title={m.settings_credits_title({}, { locale })}
      >
        <IconCoins className="size-4 text-amber-500" />
        <span
          aria-live="polite"
          className="min-w-4 text-center font-medium tabular-nums"
        >
          {balanceLabel}
        </span>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 gap-3 p-3">
        {DAILY_CHECK_IN_ENABLED ? (
          <>
            <PopoverHeader>
              <PopoverTitle className="flex items-center gap-2">
                <IconGift className="size-4 text-amber-500" />
                {m.check_in_free_rewards({}, { locale })}
              </PopoverTitle>
              <PopoverDescription>
                {m.check_in_days_completed({ days: completedDays }, { locale })}
              </PopoverDescription>
            </PopoverHeader>
            <div className="h-1.5 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-amber-400 transition-[width]"
                style={{ width: `${progress}%` }}
              />
            </div>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                openDailyCheckInDialog({
                  callbackUrl: location.href,
                  source: 'credit_badge',
                });
              }}
              className="inline-flex h-9 w-full items-center justify-center gap-2 rounded-md bg-foreground px-3 font-semibold text-background text-sm transition hover:opacity-90"
            >
              <IconGift className="size-4" />
              {checkInQuery.data?.checkedInToday
                ? m.check_in_today_complete({}, { locale })
                : m.check_in_free_rewards({}, { locale })}
            </button>
          </>
        ) : (
          <PopoverHeader>
            <PopoverTitle className="flex items-center gap-2">
              <IconCoins className="size-4 text-amber-500" />
              {balanceLabel} {m.settings_credits_title({}, { locale })}
            </PopoverTitle>
          </PopoverHeader>
        )}
        <Link
          to={Routes.SettingsCredits}
          search={{ returnTo: location.href }}
          className="inline-flex h-8 items-center justify-center gap-2 rounded-md text-muted-foreground text-xs transition hover:bg-muted hover:text-foreground"
        >
          <IconSettings className="size-3.5" />
          {m.settings_credits_title({}, { locale })}
        </Link>
      </PopoverContent>
    </Popover>
  );
}
